import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Autocomplete, TextField, FormControlLabel, Checkbox,
  List, ListItem, ListItemText, IconButton, Typography, Box, CircularProgress, Alert, Button
} from '@mui/material'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import styles from './ContactsPanel.module.scss'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Inline panel (not a dialog) that shows the billing customer's Stripe state,
 * active contacts, and an add-contact form. Renders inside a tab on the
 * consolidated admin billing view.
 */
const ContactsPanel = ({ customer, onToast, onChanged }) => {
  const [contacts, setContacts] = useState([])
  const [eligibleUsers, setEligibleUsers] = useState([])
  const [stripeInfo, setStripeInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [busyAction, setBusyAction] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [setAsPrimary, setSetAsPrimary] = useState(false)
  const [error, setError] = useState(null)

  const customerId = customer?.id

  const refresh = useCallback(async () => {
    if (!customerId) return
    try {
      setLoading(true)
      setError(null)
      const [contactsRes, usersRes, stripeRes] = await Promise.all([
        authenticatedFetchJson(apiEndpoints.billingCustomerContacts(customerId)),
        authenticatedFetchJson(apiEndpoints.billingCustomerEligibleUsers(customerId)),
        authenticatedFetchJson(apiEndpoints.billingCustomerStripeInfo(customerId)).catch(() => null)
      ])
      setContacts(contactsRes?.data || [])
      setEligibleUsers(usersRes?.data || [])
      setStripeInfo(stripeRes?.data || null)
      setSelectedUser(null)
      setSetAsPrimary(false)
    } catch (err) {
      logger.error('Failed to load contacts:', err)
      setError(err.message || 'Failed to load contacts')
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => {
    if (customerId) refresh()
  }, [customerId, refresh])

  const stripeEmailNotInContacts = stripeInfo?.email &&
    !contacts.some(c => c.email && c.email.toLowerCase() === stripeInfo.email.toLowerCase())

  const handleAdd = async () => {
    if (!selectedUser) return
    let body
    if (typeof selectedUser === 'string') {
      const email = selectedUser.trim().toLowerCase()
      if (!EMAIL_RE.test(email)) {
        onToast('Invalid email — please enter a valid email address', 'error')
        return
      }
      body = { email, is_primary: setAsPrimary }
    } else {
      body = { user_id: selectedUser.user_id, is_primary: setAsPrimary }
    }
    try {
      setBusyAction('adding')
      const response = await authenticatedFetchJson(
        apiEndpoints.billingCustomerContacts(customerId),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      )
      const sync = response?.stripe_sync
      if (sync?.synced && sync.email) {
        onToast(`Contact added — Stripe synced (${sync.email})`, 'success')
      } else if (sync?.synced) {
        onToast('Contact added — Stripe synced (no email yet)', 'warning')
      } else if (sync?.error) {
        onToast(`Contact added but Stripe sync failed: ${sync.error}`, 'warning')
      } else {
        onToast('Contact added', 'success')
      }
      onChanged?.()
      await refresh()
    } catch (err) {
      logger.error('Failed to add contact:', err)
      onToast(`Failed to add contact: ${err.message || 'unknown error'}`, 'error')
    } finally {
      setBusyAction(null)
    }
  }

  const handleRemove = async (contactId) => {
    try {
      setBusyAction(`removing-${contactId}`)
      const response = await authenticatedFetchJson(
        apiEndpoints.billingCustomerContact(customerId, contactId),
        { method: 'DELETE' }
      )
      const sync = response?.data?.stripe_sync
      const wasPrimary = response?.data?.was_primary
      if (wasPrimary && sync?.synced && !sync.email) {
        onToast('Primary contact removed — Stripe email cleared. Add a new primary contact.', 'warning')
      } else if (sync?.synced) {
        onToast('Contact removed and Stripe synced', 'success')
      } else if (sync?.error) {
        onToast(`Contact removed but Stripe sync failed: ${sync.error}`, 'warning')
      } else {
        onToast('Contact removed', 'success')
      }
      onChanged?.()
      await refresh()
    } catch (err) {
      logger.error('Failed to remove contact:', err)
      onToast(`Failed to remove contact: ${err.message || 'unknown error'}`, 'error')
    } finally {
      setBusyAction(null)
    }
  }

  return (
    <div className={styles.panel} data-testid='contacts-panel'>
      {loading && (
        <Box className={styles.loadingBox}>
          <CircularProgress size={24} />
          <Typography variant='body2'>Loading contacts...</Typography>
        </Box>
      )}

      {error && <Alert severity='error' className={styles.alert}>{error}</Alert>}

      {!loading && !error && (
        <div className={styles.grid}>
          {/* Left column: Stripe info + Active contacts */}
          <div className={styles.leftCol}>
            {stripeInfo && (
              <Box className={styles.stripePanel} data-testid='stripe-info-panel'>
                <Typography variant='subtitle2' className={styles.sectionTitle}>
                  Currently in Stripe
                </Typography>
                {stripeInfo.not_found_in_stripe ? (
                  <Alert severity='error' className={styles.alert}>
                    Stripe customer ID is set but not found in Stripe.
                    Click &ldquo;Sync to Stripe&rdquo; above to recreate it.
                  </Alert>
                ) : !stripeInfo.stripe_customer_id ? (
                  <Typography variant='body2' className={styles.muted}>
                    No Stripe customer linked yet.
                  </Typography>
                ) : (
                  <>
                    <div className={styles.stripeRow}>
                      <span className={styles.stripeLabel}>Email</span>
                      {stripeInfo.email
                        ? <strong>{stripeInfo.email}</strong>
                        : <span className={styles.muted}>(none)</span>}
                    </div>
                    <div className={styles.stripeRow}>
                      <span className={styles.stripeLabel}>Name</span>
                      {stripeInfo.name || <span className={styles.muted}>(none)</span>}
                    </div>
                    {stripeEmailNotInContacts && (
                      <Alert severity='warning' className={styles.alert}>
                        Stripe has an email that isn&apos;t in your contacts list.
                        Add a contact with this email to keep them in sync.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}

            <div className={styles.section}>
              <Typography variant='subtitle2' className={styles.sectionTitle}>
                Active contacts ({contacts.length})
              </Typography>
              {contacts.length === 0 ? (
                <Alert severity='warning' className={styles.alert}>
                  No billing contacts. Invoices cannot be sent until a primary contact is added.
                </Alert>
              ) : (
                <List dense className={styles.contactsList}>
                  {contacts.map(c => (
                    <ListItem
                      key={c.id}
                      data-testid={`contact-row-${c.id}`}
                      secondaryAction={
                        <IconButton
                          edge='end'
                          size='small'
                          disabled={busyAction === `removing-${c.id}`}
                          onClick={() => handleRemove(c.id)}
                          data-testid={`remove-contact-${c.id}`}
                          aria-label='Remove contact'
                        >
                          {busyAction === `removing-${c.id}` ? <CircularProgress size={16} /> : '✕'}
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <span>
                            {c.name && c.name !== c.email ? c.name : c.email}
                            {c.is_primary && <span className={styles.primaryBadge}>PRIMARY</span>}
                          </span>
                        }
                        secondary={c.name && c.name !== c.email ? c.email : null}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          </div>

          {/* Right column: Add contact form */}
          <div className={styles.rightCol}>
            <div className={styles.section}>
              <Typography variant='subtitle2' className={styles.sectionTitle}>
                Add a contact
              </Typography>
              <Box className={styles.addForm}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={eligibleUsers}
                  value={selectedUser}
                  onChange={(_e, value) => setSelectedUser(value)}
                  onInputChange={(_e, value, reason) => {
                    if (reason === 'input') setSelectedUser(value)
                  }}
                  getOptionLabel={(u) => {
                    if (typeof u === 'string') return u
                    return u?.name ? `${u.name} (${u.email})` : u?.email || ''
                  }}
                  isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.toLowerCase().trim()
                    if (!input) return options
                    return options.filter(u =>
                      u.email?.toLowerCase().includes(input) ||
                      u.name?.toLowerCase().includes(input)
                    )
                  }}
                  groupBy={(u) => u.suggested ? 'Suggested for this account' : 'All other users'}
                  classes={{ paper: styles.autocompletePaper, listbox: styles.autocompleteListbox }}
                  renderOption={(props, u) => (
                    <li
                      {...props}
                      key={u.user_id}
                      data-testid={`user-option-${u.user_id}`}
                      className={`${props.className || ''} ${styles.userOption} ${u.suggested ? styles.userOptionSuggested : ''}`}
                    >
                      <div className={styles.userOptionMain}>
                        <span className={styles.userOptionName}>
                          {u.name || u.email}
                        </span>
                        {u.name && <span className={styles.userOptionEmail}>{u.email}</span>}
                      </div>
                      {u.suggested && <span className={styles.suggestedBadge}>Suggested</span>}
                    </li>
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <div className={styles.groupHeader}>{params.group}</div>
                      <ul className={styles.groupList}>{params.children}</ul>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Search users or type a new email'
                      placeholder='alice@example.com'
                      helperText='Pick from the list, or type a new email to add it directly.'
                      inputProps={{ ...params.inputProps, 'data-testid': 'user-search-input' }}
                    />
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={setAsPrimary}
                      onChange={(e) => setSetAsPrimary(e.target.checked)}
                      data-testid='primary-checkbox'
                    />
                  }
                  label='Set as primary (replaces any existing primary)'
                />
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleAdd}
                  disabled={!selectedUser || busyAction === 'adding'}
                  data-testid='add-contact-btn'
                  className={styles.addButton}
                >
                  {busyAction === 'adding' ? 'Adding…' : 'Add Contact'}
                </Button>
              </Box>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

ContactsPanel.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.number,
    account_name: PropTypes.string
  }).isRequired,
  onToast: PropTypes.func.isRequired,
  onChanged: PropTypes.func
}

export default ContactsPanel
